import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { LoginScreen } from './LoginScreen';
import { apiClient } from '../api/client';
import { Button } from '../components/atoms/Button';
import { env } from '../env';
import { getStoredTokens } from './tokenStore';

type ProfileState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ok'; role: string }
  | { status: 'missing' }
  | { status: 'error'; error: DebugError };

interface DebugError {
  summary: string;
  details: Record<string, unknown>;
}

/**
 * Gate that renders children only after:
 *   1. Auth0 session confirmed (useAuth)
 *   2. Backend user profile confirmed via GET /api/v1/user/profile
 *
 * On error, shows a structured debug block with the full failure details so
 * we can diagnose CORS / network / token issues without relying on DevTools
 * inside Word's WebView (which is hard to attach on Mac).
 */
export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { state, logout } = useAuth();
  const [profile, setProfile] = useState<ProfileState>({ status: 'idle' });

  useEffect(() => {
    if (state.status !== 'authenticated') return;
    let cancelled = false;
    setProfile({ status: 'checking' });

    const timeoutMs = 15_000;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const requestStartedAt = Date.now();

    // Sanity snapshot — captured at request time so the error view can show
    // exactly what the add-in knew about auth when the failure happened.
    const stored = getStoredTokens();
    const snapshot = {
      apiUrl: env.apiUrl,
      auth0Domain: env.auth0.domain,
      auth0ClientId: env.auth0.clientId,
      auth0Audience: env.auth0.audience,
      hasStoredToken: !!stored,
      accessTokenPrefix: stored?.accessToken?.slice(0, 24) ?? null,
      tokenExpiresAt: stored ? new Date(stored.expiresAt).toISOString() : null,
      tokenExpiresInSec: stored ? Math.round((stored.expiresAt - Date.now()) / 1000) : null,
    };

    apiClient
      .get('/api/v1/user/profile', { signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        const role =
          (res.data && (res.data.role || (res.data.data && res.data.data.role))) || 'user';
        setProfile({ status: 'ok', role });
      })
      .catch((err) => {
        if (cancelled) return;
        const elapsedMs = Date.now() - requestStartedAt;
        const status = err?.response?.status;
        if (status === 404) {
          setProfile({ status: 'missing' });
          return;
        }

        const summary =
          err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED'
            ? `Timed out after ${elapsedMs}ms`
            : status
              ? `HTTP ${status}`
              : err?.message || 'Unknown error';

        const details: Record<string, unknown> = {
          ...snapshot,
          elapsedMs,
          errorName: err?.name,
          errorCode: err?.code,
          errorMessage: err?.message,
          responseStatus: status,
          responseData: err?.response?.data,
          responseHeaders: err?.response?.headers,
        };

        // eslint-disable-next-line no-console
        console.error('[AuthGate] profile fetch failed', err, details);
        setProfile({ status: 'error', error: { summary, details } });
      })
      .finally(() => clearTimeout(timer));

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [state.status]);

  if (state.status === 'loading') return <Splash message="Loading…" />;
  if (state.status === 'unauthenticated') return <LoginScreen />;

  if (profile.status === 'checking' || profile.status === 'idle') {
    return <Splash message="Signing in…" escape={logout} />;
  }

  if (profile.status === 'missing') {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 py-10 text-center">
        <h2 className="text-base font-semibold mb-2">Account not found</h2>
        <p className="text-sm text-muted-foreground mb-6 max-w-xs">
          We couldn&apos;t find a NyayAssist account for this address. Please sign up at
          app.nyayassist.ai first, then return to Word.
        </p>
        <Button variant="outline" onClick={logout}>
          Sign out
        </Button>
        <a
          href="https://app.nyayassist.ai"
          target="_blank"
          rel="noreferrer"
          className="mt-3 text-xs text-primary underline"
        >
          Go to app.nyayassist.ai
        </a>
      </div>
    );
  }

  if (profile.status === 'error') {
    return (
      <div className="flex flex-col h-full overflow-y-auto px-4 py-6">
        <h2 className="text-base font-semibold mb-1">Something went wrong</h2>
        <p className="text-sm text-red-600 mb-4 break-words">{profile.error.summary}</p>

        <div className="flex gap-2 mb-4">
          <Button onClick={() => setProfile({ status: 'idle' })}>Retry</Button>
          <Button variant="outline" onClick={logout}>Sign out</Button>
        </div>

        <details open className="text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium text-foreground mb-2">
            Debug details
          </summary>
          <pre className="whitespace-pre-wrap break-all bg-muted/40 p-2 rounded-md font-mono text-[10px] leading-tight">
            {JSON.stringify(profile.error.details, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  return <>{children}</>;
}

function Splash({ message, escape }: { message: string; escape?: () => void }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="flex items-center gap-3 text-muted-foreground text-sm">
        <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
        {message}
      </div>
      {escape && (
        <button
          type="button"
          onClick={escape}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Cancel and sign out
        </button>
      )}
    </div>
  );
}
